import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { withProcedureWraper } from "../../..";
import { getEnv } from "../../../../../AppConfigs";
import { Routes } from "../../../../../AppRoutes";
import { Icon } from "../../../../../components/icon";
import { approverType } from "../../../../../constants/itCodeRequestConstants";
import { VehicleRequestService } from "../../../../../services/company-office/VehicleRequestService";
import { useSelector } from "../../../../../store/store";
import { ProcessContextProvider, ProcessContextType } from "../../../process-context";
import OfficialVehicleInformation from "./information";
import OfficialVehicleProgress from "./progress";
import { useQueryClient } from "@tanstack/react-query";

export type ExtraStateContext = {
  pickedItHead: any;
  onApproverChange: (selectedOptions: any) => void;
};

export const siteUrl = getEnv("REACT_APP_APPROVER_BUSINESS_VEHICLE_REQUEST_URL");

export const officialVehicleDetailPage: FC = withProcedureWraper(() => {
  /**
   * Selectors
   */
  const queryClient = useQueryClient();
  const user = useSelector((state) => state.user);
  const currentUserName = user?.current?.username;

  const history = useHistory();
  const { id } = useRouteMatch().params as any;

  /**
   * States
   */
  const [officialVehicleRequest, setOfficialVehicleRequest] = useState(null as any);
  const [comment, setComment] = useState("");
  const [approvalType, setApprovalType] = useState(0);
  const [approvalStep, setApprovalStep] = useState(approverType.approver);
  const [pickedItHead, setPickedItHead] = useState<any>(null);

  const onApproverChange = useCallback((selectedOptions: any) => {
    setPickedItHead(selectedOptions ? selectedOptions : {});
  }, []);

  const checkApproverType = useCallback(() => {
    const { eApprovedBy, eApprovedByITLead, eApprovedByITHead, eAssignee, eApprovedStatus, eApprovedByITLeadStatus, eApprovedByITHeadStatus, eAssigneeStatus } =
      officialVehicleRequest?.fields;
    const approverEmail = eApprovedBy ? eApprovedBy.split("|")[0] : "";
    const approvedByITLeadEmail = eApprovedByITLead ? eApprovedByITLead.split("|")[0] : "";
    const approvedByITHeadEmail = eApprovedByITHead ? eApprovedByITHead.split("|")[0] : "";
    const assigneeEmail = eAssignee ? eAssignee.split("|")[0] : "";
    if (currentUserName.toLowerCase() === approverEmail.toLowerCase() && !eApprovedStatus) {
      setApprovalType(approverType.approver);
      return;
    }
    if (currentUserName.toLowerCase() === approvedByITLeadEmail.toLowerCase() && !eApprovedByITLeadStatus) {
      setApprovalType(approverType.itlead);
      return;
    }
    if (currentUserName.toLowerCase() === approvedByITHeadEmail.toLowerCase() && !eApprovedByITHeadStatus) {
      setApprovalType(approverType.ithead);
      return;
    }
    if (currentUserName.toLowerCase() === assigneeEmail.toLowerCase() && !eAssigneeStatus) {
      setApprovalType(approverType.assignee);
      return;
    }
    setApprovalType(0);
  }, [currentUserName, officialVehicleRequest?.fields]);

  const checkApproverStep = useCallback(() => {
    const { eApprovedStatus, eApprovedByITLeadStatus, eApprovedByITHeadStatus, eAssigneeStatus } =
      officialVehicleRequest?.fields;
    if (!eApprovedStatus) {
      setApprovalStep(approverType.approver);
      return;
    }
    if (!eApprovedByITLeadStatus) {
      setApprovalStep(approverType.itlead);
      return;
    }
    if (!eApprovedByITHeadStatus) {
      setApprovalStep(approverType.ithead);
      return;
    }
    if (!eAssigneeStatus) {
      setApprovalStep(approverType.assignee);
    }
  }, [officialVehicleRequest?.fields]);

  const handleUpdatePin = useCallback( async (isPin: boolean) => {
    try {
      if (!officialVehicleRequest) return;
      const pins: any[] = officialVehicleRequest.fields.ePins ? JSON.parse(officialVehicleRequest.fields.ePins) : [];
      const index = pins.findIndex((pin: string) => pin === currentUserName?.toLowerCase());
      if (!isPin && index < 0) return;
      if (isPin && index > -1) return;
      if (!isPin && index > -1) pins.splice(index, 1);
      if (isPin && index < 0) pins.push(currentUserName?.toLowerCase());

      const fields = {
        ePins: JSON.stringify(pins),
      };

      await VehicleRequestService.updateVehicleRequest(id, fields);
      queryClient.invalidateQueries({ queryKey: ["vehicle-travel-card-requests"] });
    } catch (error) {
      console.log(error);
    }

  },[currentUserName, id, officialVehicleRequest])

  const isPin = useCallback(() => {
    if (!officialVehicleRequest) return false;
    const pins: any[] = officialVehicleRequest.fields.ePins ? JSON.parse(officialVehicleRequest.fields.ePins) : [];
    return pins.findIndex((pin: string) => pin === currentUserName?.toLowerCase()) >= 0;
  },[currentUserName, officialVehicleRequest])

  const handleGoBack = () => {
    if(history.action === 'PUSH') 
      history.goBack();
    else
      history.push(Routes.officialVehicleListPage.path)
  }

  useEffect(() => {
    VehicleRequestService.getVehicleRequest(id)
      .then((res) => {
        setOfficialVehicleRequest(res);
        // if (res.fields) {
        //   setComment(res.fields.eApprovalComments);
        // }
      })
      .catch((err) => console.log(err.message));
  }, [id]);

  useEffect(() => {
    if (officialVehicleRequest) {
      checkApproverType();
      checkApproverStep();
    }
  }, [checkApproverStep, checkApproverType, officialVehicleRequest]);

  const ctx = useMemo<ProcessContextType>(
    () => ({
      Request: officialVehicleRequest,
      approvalStep,
      approvalType,
      comment,
      setComment,
      pickedItHead,
      onApproverChange,
      updatePin: handleUpdatePin,
      isPin: isPin,
    }),
    [approvalStep, approvalType, comment, officialVehicleRequest,pickedItHead, onApproverChange, handleUpdatePin, isPin]
  );

  return (
    <div className="ItCodeGrantedDetailPage pd15">
      <div className="PageHead">
        <div className="title">
          <div
            className="btnBack"
            onClick={handleGoBack}
          >
            <Icon.ArrowLeft />
          </div>
          {"Xem Xét Yêu Cầu Duyệt"}
        </div>
      </div>

      <ProcessContextProvider context={ctx}>
        <OfficialVehicleProgress />
        <OfficialVehicleInformation />
      </ProcessContextProvider>
    </div>
  );
});
